export const unique = <T>(items: T[]): T[] => {
    return items.filter((elem, index, self) => self.indexOf(elem) === index);
};

export const toggle = <T>(items: T[], item: T): T[] => {
    return items.includes(item) ? items.filter((i) => i !== item) : items.concat(item);
};

export const prefectureShortName = (s: string) => {
    return s.match(/.*[都府県]$/) ? s.slice(0, -1) : s;
};

export const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();

    return `${y}年${m}月${d}日`;
};

export const priceFormat = (price: number, unit = '万円') => {
    if (!price || Number(price) === 0) return `-${unit}`;
    return price.toLocaleString() + unit;
};

export const totalBudgetFormat = (totalBudget, numberOfSellingOffers, isInitialOffering) => {
    if (totalBudget && numberOfSellingOffers > 0)
        return isInitialOffering ? priceFormat(totalBudget, '') : `〜${priceFormat(totalBudget, '')}`;
    else if (numberOfSellingOffers <= 0 && isInitialOffering) return priceFormat(totalBudget, '');
    else return '-';
};

// 手数料込み総予算を計算
export const calcTotalBudgetWithCommission = (totalBudget, sellingPrice) => {
    const cleanedTotalBudget = parseBudget(totalBudget);

    return cleanedTotalBudget + calcCommission(sellingPrice);
};

// 手数料を計算
export const calcCommission = (sellingPrice) => {
    const cleanedSellingPrice = parseBudget(sellingPrice);
    return cleanedSellingPrice <= 275 ? 5.5 : cleanedSellingPrice * 0.02;
};

const parseBudget = (totalBudget) => {
    if (totalBudget == null) return null;
    const budgetString = typeof totalBudget === 'number' ? totalBudget.toString() : totalBudget;
    return parseFloat(budgetString.replace(/,/g, ''));
};

// 内閣府が発表した祝日データから抜粋
// ref: https://www8.cao.go.jp/chosei/shukujitsu/gaiyou.html
const JP_HOLIDAYS = [
    '2023/1/1',
    '2023/1/2',
    '2023/1/9',
    '2023/2/11',
    '2023/2/23',
    '2023/3/21',
    '2023/4/29',
    '2023/5/3',
    '2023/5/4',
    '2023/5/5',
    '2023/7/17',
    '2023/8/11',
    '2023/9/18',
    '2023/9/23',
    '2023/10/9',
    '2023/11/3',
    '2023/11/23',
    '2024/1/1',
    '2024/1/8',
    '2024/2/11',
    '2024/2/12',
    '2024/2/23',
    '2024/3/20',
    '2024/4/29',
    '2024/5/3',
    '2024/5/4',
    '2024/5/5',
    '2024/5/6',
    '2024/7/15',
    '2024/8/11',
    '2024/8/12',
    '2024/9/16',
    '2024/9/22',
    '2024/9/23',
    '2024/10/14',
    '2024/11/3',
    '2024/11/4',
    '2024/11/23',
    '2024/12/30',
    '2024/12/31',
    '2025/1/1',
    '2025/1/2',
    '2025/1/3',
];

export const isHoliday = (date: Date): boolean => {
    const formatted = date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    return JP_HOLIDAYS.includes(formatted);
};

export const isWeekend = (date: Date): boolean => {
    return [6, 7].includes(date.getDay());
};

export const isBusinessHour = (date: Date): boolean => {
    const hour = date.getHours();
    return hour >= 9 && hour <= 17;
};

// SSR時（コンテナのTZ。多くの場合UTC）とCSR時（日本のユーザーのローカル時刻=JST）で
// isBusinessTime()の判定基準がずれるとhydrationミスマッチになる
// （isProduction()のように「SSR時は固定値を返す」だけだと、今度は
// 「SSRは固定値・CSRは実際の値」という同種のミスマッチを形を変えて残すだけになるため採用しない）。
// そこでIntl.DateTimeFormatにtimeZone: 'Asia/Tokyo'を明示して現在時刻の年月日時分秒を取り出し、
// それをローカルのDateコンストラクタへ渡し直す。isHoliday/isWeekend/isBusinessHourはいずれも
// タイムゾーンを指定せず実行環境のローカルTZで解釈する（getDate/getDay/getHours/
// toLocaleDateStringのデフォルト挙動）ため、この「JSTの年月日時分秒をローカル時刻として
// 詰め直したDate」を渡せば、実行環境の実際のTZに関わらずJST基準の判定結果になる。
// mini_racer（SSR実行環境）でtimeZone: 'Asia/Tokyo'が正しく解決されることは実行検証済み。
// また hour12: false は深夜0時に "24" を返す実装差異が確認できたため、
// 0-23で確定するhourCycle: 'h23'を使っている。
const nowInJst = (): Date => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
        .formatToParts(new Date())
        .reduce<Record<string, string>>((acc, part) => {
            acc[part.type] = part.value;
            return acc;
        }, {});

    return new Date(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
    );
};

export const isBusinessTime = () => {
    const currentTime = nowInJst();
    return !isHoliday(currentTime) && !isWeekend(currentTime) && isBusinessHour(currentTime);
};

// NOTE: 本番環境かどうかを判定する (process.env.NODE_ENV だと Staging 環境も同様の為)
export const isProduction = () => {
    if (typeof window === 'undefined') return false;
    const domain = window.location.hostname;
    return domain === 'juchi.jp';
};

// 名義書換停止中を含む場合、削除する
export const cleanCourseName = (courseName: string): string => {
    return courseName.replace('（名義書換停止中）', '');
};
